// React hooks for the store. Importing this file requires React in the consumer;
// the rest of @biz-kid/store is React-free, so non-React contexts can stay lean.

import { createContext, useContext, type ReactNode, createElement } from 'react';
import { useStore } from 'zustand';
import type { GameState } from '@biz-kid/engine';
import type { GameStore, GameStoreInstance } from './store.js';

const StoreContext = createContext<GameStoreInstance | null>(null);

export type GameStoreProviderProps = {
  store: GameStoreInstance;
  children: ReactNode;
};

export function GameStoreProvider({ store, children }: GameStoreProviderProps) {
  return createElement(StoreContext.Provider, { value: store }, children);
}

function useGameStoreInstance(): GameStoreInstance {
  const s = useContext(StoreContext);
  if (!s) throw new Error('GameStoreProvider missing — wrap your tree in <GameStoreProvider store={...}>.');
  return s;
}

/** Pick a slice with a memoising selector — re-renders only when the slice changes. */
export function useGameStore<T>(selector: (s: GameStore) => T): T {
  const store = useGameStoreInstance();
  return useStore(store, selector);
}

export function useGameState<T>(selector: (g: GameState) => T): T {
  return useGameStore((s) => selector(s.game));
}

export function useDispatch(): GameStore['dispatch'] {
  return useGameStore((s) => s.dispatch);
}

export function useDrainOutbound(): GameStore['drainOutbound'] {
  return useGameStore((s) => s.drainOutbound);
}
